import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import AccessDenied from '@/components/ui/AccessDenied';
import Head from '@/components/ui/Head';

const Checking = () => (
  <div className="flex items-center justify-center py-24">
    <div
      className="w-8 h-8 border-4 border-gray-200 border-t-emerald-600 rounded-full animate-spin"
      aria-hidden="true"
    />
  </div>
);

/**
 * Route gate for admin-only areas.
 * Renders its children only for signed-in admins — the protected page never
 * mounts (and never loads its data) for anyone else.
 */
export default function AdminOnly({ children }) {
  const { user, isLoadingAuth, isLoadingPublicSettings, navigateToLogin } = useAuth();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let active = true;

    const verify = async () => {
      try {
        const currentUser = user || (await base44.auth.me());
        if (active) setStatus(currentUser?.role === 'admin' ? 'admin' : 'denied');
      } catch {
        if (active) setStatus('guest');
      }
    };

    if (!isLoadingAuth && !isLoadingPublicSettings) verify();
    return () => {
      active = false;
    };
  }, [isLoadingAuth, isLoadingPublicSettings, user]);

  // Admin areas must stay out of search results. The app is a SPA, so these
  // URLs are reachable by anyone and would otherwise inherit the global
  // "index, follow" from index.html.
  return (
    <>
      <Head meta={[['name', 'robots', 'noindex, nofollow']]} />
      {status === 'checking' ? (
        <Checking />
      ) : status === 'admin' ? (
        children
      ) : status === 'guest' ? (
        <AccessDenied variant="login" onLogin={navigateToLogin} />
      ) : (
        <AccessDenied variant="denied" />
      )}
    </>
  );
}